import { useState, FormEvent, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTable } from '../../hooks/useTableManagement';
import { useTableCheckIn, useGameTypes } from '../../hooks/useTableCheckIn';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import './CheckInPage.css';

export function CheckInPage() {
  const { tableId, qrType } = useParams<{ tableId: string; qrType: 'player1' | 'player2' }>();
  const navigate = useNavigate();

  const { data: table, isLoading: isLoadingTable } = useTable(tableId || '');
  const { data: gameTypesData, isLoading: isLoadingGameTypes } = useGameTypes();
  const { checkIn, isCheckingIn, checkInData, error } = useTableCheckIn(tableId || '');

  const [playerName, setPlayerName] = useState('');
  const [gameType, setGameType] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const gameTypes = gameTypesData?.gameTypes || [];

  // Initialize game type selection with first available game type
  useEffect(() => {
    if (gameTypes.length > 0 && !gameType) {
      setGameType(gameTypes[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameTypesData, gameType]);

  // Redirect to countdown if match found
  useEffect(() => {
    if (checkInData && !checkInData.waitingForMatch && checkInData.match?.matchId) {
      const player1Name = encodeURIComponent(playerName);
      const player2Name = encodeURIComponent(checkInData.match.playerName);
      navigate(`/table/${tableId}/countdown/${checkInData.match.matchId}/${player1Name}/${player2Name}`);
    }
  }, [checkInData, playerName, tableId, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!playerName.trim()) {
      setLocalError('Nome do jogador é obrigatório');
      return;
    }

    if (!gameType) {
      setLocalError('Tipo de jogo é obrigatório');
      return;
    }

    try {
      await checkIn({
        playerName: playerName.trim(),
        gameType,
        qrCodeType: qrType || 'player1',
      });
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Erro ao fazer check-in');
    }
  };

  if (isLoadingTable || isLoadingGameTypes) {
    return (
      <div className="checkin-page">
        <div className="checkin-page__loading">Carregando...</div>
      </div>
    );
  }

  if (!table) {
    return (
      <div className="checkin-page">
        <Card className="checkin-page__error-card">
          <h2>Mesa não encontrada</h2>
          <p>A mesa que você está tentando acessar não existe.</p>
        </Card>
      </div>
    );
  }

  if (checkInData) {
    return (
      <div className="checkin-page">
        <Card className="checkin-page__success-card">
          {checkInData.waitingForMatch ? (
            <>
              <div className="checkin-page__icon">⏳</div>
              <h2>Check-in Realizado!</h2>
              <p className="checkin-page__message">
                Olá, <strong>{playerName}</strong>! Você está aguardando outro jogador para iniciar a partida.
              </p>
              <p className="checkin-page__submessage">
                Quando outro jogador fizer check-in com o mesmo tipo de jogo, a partida será iniciada automaticamente.
              </p>
            </>
          ) : (
            <>
              <div className="checkin-page__icon">🎉</div>
              <h2>Partida Encontrada!</h2>
              <p className="checkin-page__message">
                <strong>{playerName}</strong>, você foi pareado com <strong>{checkInData.match?.playerName}</strong>!
              </p>
              <p className="checkin-page__submessage">
                A partida começará em breve. Boa sorte!
              </p>
            </>
          )}
          <Button onClick={() => navigate(`/table/${tableId}/status`)}>
            Ver Status da Mesa
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="checkin-page">
      <Card className="checkin-page__card">
        <div className="checkin-page__header">
          <h1 className="checkin-page__title">Check-in na Mesa</h1>
          <h2 className="checkin-page__table-name">{table.name}</h2>
          {table.location && (
            <p className="checkin-page__location">📍 {table.location}</p>
          )}
          <div className="checkin-page__qr-badge">
            {qrType === 'player1' ? 'Jogador 1' : 'Jogador 2'}
          </div>
        </div>

        <form className="checkin-page__form" onSubmit={handleSubmit}>
          <div className="checkin-page__field">
            <label htmlFor="playerName" className="checkin-page__label">
              Seu Nome *
            </label>
            <input
              id="playerName"
              type="text"
              className="checkin-page__input"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="Digite seu nome"
              required
              disabled={isCheckingIn}
              autoFocus
            />
          </div>

          <div className="checkin-page__field">
            <label htmlFor="gameType" className="checkin-page__label">
              Tipo de Jogo *
            </label>
            <select
              id="gameType"
              className="checkin-page__select"
              value={gameType}
              onChange={(e) => setGameType(e.target.value)}
              disabled={isCheckingIn}
            >
              {gameTypes.map((gt) => (
                <option key={gt.id} value={gt.id}>
                  {gt.name} - {gt.maxPoints} pontos
                </option>
              ))}
            </select>
            {gameTypes.find(gt => gt.id === gameType)?.description && (
              <p className="checkin-page__game-description">
                {gameTypes.find(gt => gt.id === gameType)?.description}
              </p>
            )}
          </div>

          {(localError || error) && (
            <div className="checkin-page__error">
              {localError || (error instanceof Error ? error.message : 'Erro ao fazer check-in')}
            </div>
          )}

          <Button type="submit" disabled={isCheckingIn}>
            {isCheckingIn ? 'Fazendo Check-in...' : 'Fazer Check-in'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
